import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSnackbar } from 'notistack';
import { DataGrid, GridActionsCellItem } from '@mui/x-data-grid';
import {
  Button,
  Card,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  InputAdornment,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/EditOutlined';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';

import customerCreditsApi from '../../../api/customerCreditsApi';
import { getErrorMessage } from '../../../utils/errorHandler';
import CustomerCreditFormDialog, { formatPen } from '../../../sections/admin/finance/CustomerCreditFormDialog';

// "vencido" no es un estado guardado: es un credito pendiente cuya fecha de vencimiento ya paso.
const STATUS_META = {
  pendiente: { label: 'Pendiente', color: 'default' },
  vencido: { label: 'Vencido', color: 'error' },
  pagado: { label: 'Pagado', color: 'success' },
  anulado: { label: 'Anulado', color: 'warning' },
};

const formatDate = (value) => (value ? new Date(`${value}T00:00:00`).toLocaleDateString('es-PE') : '—');

export default function CustomerCreditsListPage() {
  const { enqueueSnackbar } = useSnackbar();

  const [rows, setRows] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });

  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const [formOpen, setFormOpen] = useState(false);
  const [editingCredit, setEditingCredit] = useState(null);
  const [creditToDelete, setCreditToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Debounce del buscador para no disparar una peticion en cada tecla
  useEffect(() => {
    const timeout = setTimeout(() => {
      setSearch(searchInput);
      setPaginationModel((prev) => ({ ...prev, page: 0 }));
    }, 400);

    return () => clearTimeout(timeout);
  }, [searchInput]);

  const fetchCredits = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await customerCreditsApi.getCredits({
        search: search || undefined,
        status: statusFilter || undefined,
        page: paginationModel.page + 1,
        per_page: paginationModel.pageSize,
      });
      setRows(data.data);
      setRowCount(data.meta.total);
    } catch (error) {
      enqueueSnackbar(getErrorMessage(error, 'No se pudo cargar la lista de créditos.'), { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter, paginationModel, enqueueSnackbar]);

  useEffect(() => {
    fetchCredits();
  }, [fetchCredits]);

  const openForm = (credit) => {
    setEditingCredit(credit);
    setFormOpen(true);
  };

  const handleCloseForm = () => {
    setFormOpen(false);
    setEditingCredit(null);
  };

  const handleConfirmDelete = async () => {
    if (!creditToDelete) {
      return;
    }
    setDeleting(true);
    try {
      await customerCreditsApi.deleteCredit(creditToDelete.id);
      enqueueSnackbar('Crédito eliminado correctamente.', { variant: 'success' });
      setCreditToDelete(null);
      await fetchCredits();
    } catch (error) {
      enqueueSnackbar(getErrorMessage(error, 'No se pudo eliminar el crédito.'), { variant: 'error' });
    } finally {
      setDeleting(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        field: 'document_number',
        headerName: 'RUC / DNI',
        width: 150,
        valueGetter: (value, row) => `${row.document_type} ${row.document_number}`,
      },
      { field: 'customer_name', headerName: 'Razón social / Nombre', flex: 1, minWidth: 200 },
      {
        field: 'items',
        headerName: 'Productos',
        flex: 1,
        minWidth: 200,
        valueGetter: (value, row) =>
          row.items?.map((item) => `${item.quantity} x ${item.product?.name || 'Producto eliminado'}`).join(', ') ||
          '—',
      },
      { field: 'total', headerName: 'Total', width: 120, valueFormatter: (value) => formatPen(value) },
      { field: 'date', headerName: 'Fecha', width: 110, valueFormatter: (value) => formatDate(value) },
      { field: 'term_days', headerName: 'Plazo', width: 90, valueFormatter: (value) => `${value} días` },
      { field: 'due_date', headerName: 'Vence', width: 110, valueFormatter: (value) => formatDate(value) },
      {
        field: 'status',
        headerName: 'Estado',
        width: 120,
        valueGetter: (value, row) => (row.is_overdue ? 'vencido' : value),
        renderCell: (params) => (
          <Chip
            label={STATUS_META[params.value]?.label || params.value}
            color={STATUS_META[params.value]?.color || 'default'}
            size="small"
          />
        ),
      },
      {
        field: 'actions',
        type: 'actions',
        headerName: 'Acciones',
        width: 100,
        getActions: (params) => [
          <GridActionsCellItem key="edit" icon={<EditIcon />} label="Editar" onClick={() => openForm(params.row)} />,
          <GridActionsCellItem
            key="delete"
            icon={<DeleteIcon />}
            label="Eliminar"
            onClick={() => setCreditToDelete(params.row)}
          />,
        ],
      },
    ],
    []
  );

  return (
    <Stack spacing={3}>
      <Stack sx={{ alignItems: 'flex-start', justifyContent: 'space-between' }} direction="row" spacing={2}>
        <div>
          <Typography variant="h4">Créditos</Typography>
          <Typography variant="body2" color="text.secondary">
            Productos entregados a crédito a empresas (RUC) o personas (DNI), con plazo de 7, 15 o 30 días.
          </Typography>
        </div>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => openForm(null)}>
          Nuevo crédito
        </Button>
      </Stack>

      <Card sx={{ p: 2 }}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 8 }}>
            <TextField
              fullWidth
              label="Buscar"
              placeholder="RUC, DNI, razón social o nombre"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                },
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              select
              fullWidth
              label="Estado"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPaginationModel((prev) => ({ ...prev, page: 0 }));
              }}
            >
              <MenuItem value="">Todos</MenuItem>
              {Object.entries(STATUS_META).map(([value, meta]) => (
                <MenuItem key={value} value={value}>
                  {meta.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>
      </Card>

      <Card>
        <DataGrid
          autoHeight
          rows={rows}
          columns={columns}
          loading={loading}
          disableRowSelectionOnClick
          paginationMode="server"
          rowCount={rowCount}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 25, 50]}
          onRowClick={(params) => openForm(params.row)}
        />
      </Card>

      <CustomerCreditFormDialog open={formOpen} onClose={handleCloseForm} onSaved={fetchCredits} credit={editingCredit} />

      <Dialog open={Boolean(creditToDelete)} onClose={() => setCreditToDelete(null)}>
        <DialogTitle>Eliminar crédito</DialogTitle>
        <DialogContent>
          <DialogContentText>
            ¿Seguro que deseas eliminar el crédito de <strong>{creditToDelete?.customer_name}</strong>? Esta acción no
            se puede deshacer.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreditToDelete(null)} disabled={deleting}>
            Cancelar
          </Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained" disabled={deleting}>
            {deleting ? 'Eliminando...' : 'Eliminar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
